import React, { useEffect, useState, useCallback } from "react";
import { Sentinel } from "../../model_gen/sentinel";
import { SentinelService } from "../../services/SentinelService";
import {
  Col,
  notification,
  Popover,
  Row,
  Table,
  Tag,
  Typography,
  Empty,
} from "antd";
import moment from "moment/moment";
import {
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../PermissionsProvider";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import TableDropDownMenu from "../shared/tablemenus/TableDropdownMenu";
import { SentinelListProps, SeverityLevel } from "../../types/ISentinels";
import { PrimaryButton } from "../shared/button/Buttons";

const { Title } = Typography;

type SentinelColumns = ColumnsType<Sentinel>;

const SentinelList: React.FC<SentinelListProps> = ({
  intentGroupId,
  emptyText,
  excludeHeader = false,
  onSentinelClick,
}) => {
  const { apiKey } = usePermissions();

  const [sentinels, setSentinels] = useState<Sentinel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(5);

  // Cache key based on current filters
  const getCacheKey = useCallback(() => {
    return `sentinels_${intentGroupId || ""}_${currentPage}_${pageSize}`;
  }, [intentGroupId, currentPage, pageSize]);

  const severityColorMap: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "default",
    [SeverityLevel.INFO]: "default",
    [SeverityLevel.WARN]: "warning",
    [SeverityLevel.ERROR]: "warning",
    [SeverityLevel.FATAL]: "error",
  };

  const severityPopovers: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.INFO]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.WARN]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.ERROR]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.FATAL]: "This Sentinel is evaluating requests.",
  };

  const navigate = useNavigate();

  const getSentinels = useCallback(() => {
    const cacheKey = getCacheKey();
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, totalCount } = JSON.parse(cachedData);
      setSentinels(data);
      setTotal(totalCount);
      setIsLoading(false);
      return;
    }

    if (intentGroupId) {
      SentinelService.getSentinelsByIntentGroup(
        intentGroupId,
        setIsLoading,
        apiKey,
        currentPage,
        pageSize
      ).subscribe(
        (response: { data: Sentinel[]; total: number }) => {
          const sortedSentinels = response.data.sort((a, b) => b.severity - a.severity);
          setSentinels(sortedSentinels);
          setTotal(response.total);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: sortedSentinels,
            totalCount: response.total
          }));
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    }
  }, [intentGroupId, apiKey, currentPage, pageSize, getCacheKey]);

  // Clear cache when component unmounts
  useEffect(() => {
    return () => {
      const cacheKey = getCacheKey();
      sessionStorage.removeItem(cacheKey);
    };
  }, [getCacheKey]);

  const deleteSentinel = (sentinelId: number) => {
    SentinelService.deleteSentinel(sentinelId, setIsLoading, apiKey).subscribe(
      () => {
        // Remove the deleted sentinel from the list
        const updatedSentinels = sentinels.filter(
          (sent) => sent.id !== sentinelId,
        );
        setSentinels(updatedSentinels);
        notification.success({
          message: "Success",
          description: "Sentinel deleted successfully",
        });
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to delete sentinel",
        });
      },
    );
  };

  const sentinelColumns: SentinelColumns = [
    {
      title: "Name",
      dataIndex: "sentinel_name",
      key: "sentinel_name",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (state) => (
        <Tag
          color={
            state === "ACTIVE"
              ? "success"
              : state === "DISABLED"
                ? "error"
                : "default"
          }>
          {state}
        </Tag>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (severity: SeverityLevel) => (
        <Popover content={severityPopovers[severity]}>
          <Tag color={severityColorMap[severity]}>
            {SeverityLevel[severity]}
          </Tag>
        </Popover>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => {
        const items = [
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (intentGroupId) {
                    navigate(`/intent-group/${intentGroupId}/sentinel/${record.id}`);
                  } else {
                    navigate(`/application/${record.application_id}/sentinel/${record.id}`);
                  }
                }}>
                Edit Sentinel
              </a>
            ),
            key: "0",
            icon: <EditOutlined />,
          },
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSentinel(record.id);
                }}>
                Delete Sentinel
              </a>
            ),
            key: "4",
            icon: <DeleteOutlined />,
          },
        ];
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <TableDropDownMenu items={items} placement="bottomLeft" />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (intentGroupId) {
      getSentinels();
    }
  }, [intentGroupId, getSentinels]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  const handleRowClick = (record: Sentinel) => {
    if (onSentinelClick) {
      onSentinelClick(record.id);
    } else if (intentGroupId) {
      navigate(`/intent-group/${intentGroupId}/sentinel/${record.id}`);
    } else {
      navigate(`/application/${record.application_id}/sentinel/${record.id}`);
    }
  };

  return (
    <>
      {!excludeHeader && (
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4}>Sentinels</Title>
          </Col>
          <Col>
            <PrimaryButton
              icon={<PlusOutlined />}
              onClick={() => {
                if (intentGroupId) {
                  navigate(`/intent-group/${intentGroupId}/sentinel/new`);
                }
              }}>
              Add Sentinel
            </PrimaryButton>
          </Col>
        </Row>
      )}
      <Table
        dataSource={sentinels}
        columns={sentinelColumns}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} items`
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
        locale={{
          emptyText: emptyText || (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No sentinels found"
            />
          ),
        }}
      />
    </>
  );
};

export default SentinelList;
